import { IAlternativeUnits, IGetAllProductsResult } from '../actions/create-order/get-all-products.action';
import { IPostCartItems } from '../actions/create-order/post-cart.action';
import { formatSliceNumber } from './format-number';

/**
 *
 * @param product the product to update
 * @param unitOfMeasure the unit to convert into
 * @param convertBackwards convert already converted value?
 * @returns
 */
export const getConversionFactor = (product: IGetAllProductsResult, unitOfMeasure: string, convertBackwards: boolean): number => {
    let converted: number;

    if (unitOfMeasure !== product.BaseUnit) {
        const alternativeUnit = product?.AlternativeUnits?.find((unit: IAlternativeUnits) => unit.Unit === unitOfMeasure);
        if (alternativeUnit) {
            converted = convertBackwards
                ? Number(alternativeUnit.Denomerator) / Number(alternativeUnit.Numerator)
                : Number(alternativeUnit.Numerator) / Number(alternativeUnit.Denomerator);
            return converted;
        }
    }
    return 1;
};

/**
 * Convert a quantity from a given unit into the product's base unit.
 *
 * @param product the product to use for conversion (contains base/sales unit info)
 * @param fromUnit the unit of the supplied quantity
 * @param quantity the quantity to convert (string or number)
 * @returns an object exposing getter functions for the converted quantity and unit,
 *          plus direct properties for convenience
 */
export const convertQuantity = (
    product: IGetAllProductsResult,
    fromUnit: string,
    quantity: string | number
): {
    getConvQty: () => number;
    getConvUnit: () => string;
    qty: number;
    unit: string;
} => {
    const unit = product?.SalesUnit || product?.BaseUnit;

    // Normalize numeric input
    const parsedQty = typeof quantity === 'string' ? Number(quantity.trim()) : Number(quantity);
    if (!Number.isFinite(parsedQty)) {
        return {
            getConvQty: () => 0,
            getConvUnit: () => unit,
            qty: 0,
            unit
        };
    }

    let convFact = 1;
    try {
        convFact = getConversionFactor(product, fromUnit, false);
        if (!Number.isFinite(convFact)) {
            convFact = 1;
        }
    } catch {
        convFact = 1;
    }

    const converted = parsedQty * convFact;
    let rounded: number;
    if (product?.SalesUnit) {
        const convertedBackwardFact = getConversionFactor(product, unit, false);
        rounded = convertedBackwardFact;
    } else {
        rounded = Number(converted.toFixed(3));
    }

    return {
        getConvQty: () => rounded,
        getConvUnit: () => unit,
        qty: rounded,
        unit
    };
};

/**
 * Determine the purchase quantity and unit to use for a cart item,
 * converting when the cart unit differs from the product's base/sales unit.
 *
 * @param productsData the product master data list to find matching product info
 * @param product the cart item (contains ProductNumber, UnitOfMeasure, Quantity)
 * @returns an object exposing getter functions for the resolved quantity and unit
 */
export const getPurchaseQuantity = (
    productsData: IGetAllProductsResult[],
    product: IPostCartItems
): {
    getQuantity: () => string;
    getUnit: () => string;
} => {
    const match = productsData.find(p => p.ProductNumber === product.ProductNumber);
    const targetUnit = match?.SalesUnit || match?.BaseUnit;

    const getQuantity = (): string => {
        // If we have a match and the units differ, convert into the product's base/sales unit
        if (match && targetUnit && product.UnitOfMeasure !== targetUnit) {
            const convertedQuantity = convertQuantity(match, product.UnitOfMeasure, product.Quantity).getConvQty();
            return formatSliceNumber(convertedQuantity.toString());
        }
        // otherwise return original quantity
        return product.Quantity;
    };

    const getUnit = (): string => {
        if (match && targetUnit && product.UnitOfMeasure !== targetUnit) {
            return targetUnit;
        }
        return product.UnitOfMeasure;
    };

    return { getQuantity, getUnit };
};
